import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoIcon from '@mui/icons-material/Info';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { ChartWrapper } from './ChartWrapper';
import { MetricTargetModal } from './MetricTargetModal';
import {
  GET_SHOPIFY_METRICS_VALUES,
  GET_MAGENTO_METRICS_VALUES,
  GET_FACEBOOK_METRICS_VALUES,
  GET_GOOGLE_ADS_METRICS_DRAFT,
  GET_GOOGLE_ANALYTICS_4_METRICS,
  GET_KIPIFY_METRICS_VALUES,
  GET_CUSTOM_FORMULA_VALUE,
  GET_DATABASE_DATA_FOR_CUSTOM_REPORT,
  GET_DB_CONNECTOR_CONFIG,
} from 'src/utils/gql/queries';
import {
  isDatabaseChartDateSplit,
  resolveDatabaseMetricDailySplit,
} from '../utils/databaseMetricDateSplit';
import {
  ConnectorType,
  GA4FilterItem,
  Metric,
  MetricValuesRow,
  MetricsByConnectorType,
} from 'src/utils/gql/types';
import { useConnectors } from 'src/providers/connectors/hook/useConnectors';
import { getIconForMetric } from 'src/utils';
import { formatNumberIT } from 'src/utils/stringUtils';
import type { KpiMetricTarget } from '../types';
import {
  getKpiTargetPeriodLabel,
  getTargetOffTrackGapAmount,
  isTargetOnTrack,
} from '../utils/targetUtils';

/**
 * Converte valori numerici anche quando arrivano già formattati (es. "€1.234,00", "12,34%", "1.234").
 * Serve soprattutto per le Custom Formula, perché il backend applica format/prefix/suffix lato server.
 */
function parseNumericValue(raw: unknown): number {
  if (raw === null || raw === undefined) return 0;
  const s = String(raw).trim();
  if (!s) return 0;

  // Tieni solo cifre e separatori base; rimuovi simboli (€, %, spazi, ecc.)
  let cleaned = s.replace(/[^\d,.-]/g, '');
  if (!cleaned) return 0;

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  // Assunzione conservativa: se compaiono sia "." che ",", trattiamo "." come separatore migliaia e "," come decimale (formato IT).
  if (hasComma && hasDot) {
    cleaned = cleaned.replace(/\./g, '').replace(/,/g, '.');
  } else if (hasComma && !hasDot) {
    // Solo virgola: usala come separatore decimale
    cleaned = cleaned.replace(/,/g, '.');
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

type KpiPoint = { date: string; value: number };

const GA4_RATIO_OR_AVERAGE_METRIC_KEYS = new Set(['averagePurchaseRevenue']);

function isGoogleAnalytics4RatioMetric(
  connectorType: ConnectorType | string,
  metricKey: string,
): boolean {
  return (
    connectorType === ConnectorType.GOOGlE_ANALYTICS4 &&
    GA4_RATIO_OR_AVERAGE_METRIC_KEYS.has(metricKey)
  );
}

/**
 * Metriche Facebook non additive sul periodo (cost-per / rate / ROAS).
 * Il totale corretto arriva da Insights senza time_increment, non dalla somma dei giorni.
 */
export function isFacebookRatioMetric(metricKey: string): boolean {
  const key = String(metricKey || '').toLowerCase();
  if (
    key === 'cpc' ||
    key === 'cpm' ||
    key === 'cpp' ||
    key === 'ctr' ||
    key === 'frequency'
  ) {
    return true;
  }
  if (key.startsWith('cost_per')) return true;
  if (key.includes('roas')) return true;
  return false;
}

/** Setting campagna Ads (budget / tCPA): non additivi sui giorni. */
export function isGoogleAdsCampaignSettingMetric(metricKey: string): boolean {
  const key = String(metricKey || '');
  return (
    key === 'campaign_budget.amount_micros' ||
    key === 'campaign.target_cpa_micros' ||
    key === 'daily_budget' ||
    key === 'target_cpa' ||
    key === 'tcpa' ||
    key.endsWith('.campaign_budget.amount_micros')
  );
}

function isGoogleAdsDailyBudgetMetric(metricKey: string): boolean {
  const key = String(metricKey || '');
  return (
    key === 'campaign_budget.amount_micros' ||
    key === 'daily_budget' ||
    key.endsWith('.campaign_budget.amount_micros')
  );
}

function getRowDateKey(row: any): string | null {
  const dateSv = row?.splitValues?.find(
    (sv: any) =>
      sv?.key === 'date' ||
      sv?.key === 'order_creation_date' ||
      sv?.key === 'segments.date',
  );
  return dateSv?.value ? String(dateSv.value) : null;
}

function daysBetweenInclusive(fromIso: string, toIso: string): number {
  const start = new Date(fromIso);
  const end = new Date(toIso);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  const ms = end.getTime() - start.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
}

function monthKeyFromIsoDate(dateIso: string): string {
  // dateIso: YYYY-MM-DD
  return String(dateIso).slice(0, 7);
}

function toUtcDateOnly(
  dateLike: string,
): { y: number; m: number; d: number } | null {
  // Accetta:
  // - "YYYY-MM-DD"
  // - "YYYY-MM-DDTHH:mm:ss.sssZ"
  const ymd = String(dateLike).slice(0, 10);
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d))
    return null;
  return { y, m, d };
}

function bucketPointsByMonth(params: {
  points: KpiPoint[];
  aggregation: 'sum' | 'avg';
}): KpiPoint[] {
  const { points, aggregation } = params;
  const acc: Record<string, { sum: number; count: number }> = {};
  for (const p of points) {
    if (!p?.date) continue;
    const mk = monthKeyFromIsoDate(p.date);
    if (!acc[mk]) acc[mk] = { sum: 0, count: 0 };
    acc[mk].sum += Number.isFinite(p.value) ? p.value : 0;
    acc[mk].count += 1;
  }
  return Object.entries(acc)
    .map(([mk, v]) => ({
      date: mk, // XAxis: YYYY-MM
      value: aggregation === 'avg' && v.count > 0 ? v.sum / v.count : v.sum,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Calcola il valore "totale" della card a partire dalle righe (anche con split per data).
 *
 * IMPORTANT:
 * - Per metriche additive (es. sales/spend) il totale è la somma dei valori giornalieri.
 * - Per metriche "ratio/average" (es. AOV, ROAS, LTV) il totale NON è la somma dei valori giornalieri:
 *   in questi casi usiamo la riga totale (marcata con `_is_total_row`) quando presente.
 */
/**
 * Variazione % vs periodo di confronto.
 * Usa `== null` (non truthy) così totale compare = 0 mostra ancora "+100% was 0".
 */
export function computeKpiCompareChange(params: {
  metricValue: number | null;
  compareMetricValue: number | null;
  isInvertedMetric?: boolean;
}): {
  percentage: number;
  value: number;
  isPositive: boolean;
} | null {
  const { metricValue, compareMetricValue, isInvertedMetric = false } = params;
  if (compareMetricValue == null || metricValue == null) {
    return null;
  }

  const previousValue = compareMetricValue;
  const currentValue = metricValue;

  if (previousValue === 0) {
    const rawIsPositive = currentValue > 0;
    const isPositive = isInvertedMetric ? !rawIsPositive : rawIsPositive;
    return {
      percentage: currentValue > 0 ? 100 : 0,
      value: previousValue,
      isPositive,
    };
  }

  const percentage = ((currentValue - previousValue) / previousValue) * 100;
  const rawIsPositive = percentage >= 0;
  const isPositive = isInvertedMetric ? !rawIsPositive : rawIsPositive;

  return {
    percentage: Math.round(percentage * 100) / 100,
    value: previousValue,
    isPositive,
  };
}

export function computeKpiCardTotalFromRows(params: {
  rows: any[];
  connectorType: ConnectorType | string;
  metricKey: string;
}): number {
  const { rows, connectorType, metricKey } = params;

  const hasDateSplit = rows.some((row: any) =>
    row.splitValues?.some(
      (sv: any) =>
        sv.key === 'date' ||
        sv.key === 'order_creation_date' ||
        sv.key === 'segments.date',
    ),
  );

  // Setting Ads: budget/tCPA non vanno sommati sui giorni (ripetono il setting ogni giorno).
  // Per giorno: somma campagne (capacity account); sul periodo: max dei totali giornalieri.
  if (
    connectorType === ConnectorType.GOOGLE_ADS &&
    isGoogleAdsCampaignSettingMetric(metricKey)
  ) {
    if (hasDateSplit) {
      const byDate = new Map<string, number>();
      for (const row of rows) {
        const isTotalRow = row.splitValues?.some(
          (sv: any) => sv?.key === '_is_total_row',
        );
        if (isTotalRow) continue;
        const dateKey = getRowDateKey(row);
        if (!dateKey) continue;
        const metricValue = row.metricValues?.find(
          (mv: any) => mv.key === metricKey,
        );
        if (metricValue?.value === undefined || metricValue?.value === null) {
          continue;
        }
        const n = parseNumericValue(metricValue.value);
        byDate.set(dateKey, (byDate.get(dateKey) || 0) + n);
      }
      if (byDate.size > 0) {
        return Math.max(...Array.from(byDate.values()));
      }
    }

    // Senza split data: budget = somma campagne; tCPA = max (non ha senso sommare target)
    let acc = 0;
    let saw = false;
    for (const row of rows) {
      const metricValue = row.metricValues?.find(
        (mv: any) => mv.key === metricKey,
      );
      if (metricValue?.value === undefined || metricValue?.value === null) {
        continue;
      }
      const n = parseNumericValue(metricValue.value);
      if (!saw) {
        acc = n;
        saw = true;
      } else if (isGoogleAdsDailyBudgetMetric(metricKey)) {
        acc += n;
      } else {
        acc = Math.max(acc, n);
      }
    }
    return saw ? acc : 0;
  }

  // IMPORTANT: elenco metriche che NON vanno sommate sui giorni.
  // LTV è una media (sales per customer) e quindi il totale corretto arriva dal backend come riga totale.
  const isRatioMetric =
    (connectorType === MetricsByConnectorType.KIPIFY &&
      (metricKey === 'markup' ||
        metricKey === 'roas' ||
        metricKey === 'acos' ||
        metricKey === 'acos_total' ||
        metricKey === 'oac' ||
        metricKey === 'cac' ||
        metricKey === 'cac_new_customers')) ||
    (connectorType === ConnectorType.SHOPIFY &&
      (metricKey === 'average_order_value' ||
        metricKey === 'life_time_value' ||
        metricKey === 'discount_rate')) ||
    (connectorType === ConnectorType.MAGENTO &&
      (metricKey === 'average_order_value' || metricKey === 'discount_rate')) ||
    // Facebook cost-per / rate: media sul periodo, non somma dei giorni.
    (connectorType === ConnectorType.FACEBOOK &&
      isFacebookRatioMetric(metricKey)) ||
    // Google Ads average_cpc è una media (cost/clicks), non una somma dei giorni.
    (connectorType === ConnectorType.GOOGLE_ADS &&
      (metricKey === 'metrics.average_cpc' ||
        metricKey === 'average_cpc' ||
        metricKey.endsWith('.average_cpc'))) ||
    isGoogleAnalytics4RatioMetric(connectorType, metricKey);

  if (isRatioMetric && hasDateSplit) {
    const totalRow = rows.find((row: any) => {
      const isMarkedTotal = row.splitValues?.some(
        (sv: any) => sv.key === '_is_total_row',
      );
      if (isMarkedTotal) return true;
      const rowHasDate = row.splitValues?.some(
        (sv: any) =>
          sv.key === 'date' ||
          sv.key === 'order_creation_date' ||
          sv.key === 'segments.date',
      );
      return !rowHasDate;
    });

    if (totalRow) {
      const metricValue = totalRow.metricValues?.find(
        (mv: any) => mv.key === metricKey,
      );
      if (metricValue?.value !== undefined && metricValue?.value !== null) {
        return parseNumericValue(metricValue.value);
      }
    }
  }

  // Solo riga totale (es. Magento senza ordini nel range): usa quel valore.
  const onlyTotalRow =
    rows.length === 1 &&
    rows[0]?.splitValues?.some((sv: any) => sv?.key === '_is_total_row');
  if (onlyTotalRow) {
    const metricValue = rows[0].metricValues?.find(
      (mv: any) => mv.key === metricKey,
    );
    if (metricValue?.value !== undefined && metricValue?.value !== null) {
      return parseNumericValue(metricValue.value);
    }
    return 0;
  }

  // Default: somma valori (escludendo righe totali quando ci sono dati per data)
  const total = rows.reduce((sum, row) => {
    if (hasDateSplit) {
      const isTotalRow = row.splitValues?.some(
        (sv: any) => sv?.key === '_is_total_row',
      );
      const rowHasDate = row.splitValues?.some(
        (sv: any) =>
          sv &&
          (sv.key === 'date' ||
            sv.key === 'order_creation_date' ||
            sv.key === 'segments.date'),
      );
      if (isTotalRow || !rowHasDate) {
        return sum;
      }
    }

    const metricValue = row.metricValues?.find(
      (mv: any) => mv.key === metricKey,
    );
    if (metricValue?.value !== undefined && metricValue?.value !== null) {
      return sum + parseNumericValue(metricValue.value);
    }
    return sum;
  }, 0);

  return total;
}

const Card = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2.5),
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e2e8f0',
  transition: 'all 0.2s ease',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const MetricInfo = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flex: 1,
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

const ChangeIndicator = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: '0.875rem',
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  height: '200px',
  width: '100%',
  minHeight: '200px',
  minWidth: 0, // Permette a ResponsiveContainer di calcolare correttamente
}));

const ConnectorIcons = styled(Box)(() => ({
  display: 'flex',
  gap: 4,
  alignItems: 'center',
}));

const ConnectorIcon = styled(Box)<{ color: string }>(() => ({
  width: 20,
  height: 20,
  borderRadius: 4,
  backgroundColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
  fontSize: '10px',
  fontWeight: 'bold',
}));

interface KPICardProps {
  id: string;
  index: number;
  metric: Metric;
  connectorType: ConnectorType | string;
  dateRange: { from: string; to: string };
  compareDateRange?: { from: string; to: string } | null;
  showChart: boolean;
  /** Filtri ordini Shopify: usati per metriche Shopify e per Markup (Kipify). */
  filters?: GA4FilterItem[];
  target?: KpiMetricTarget | null;
  onRemove: () => void;
  onToggleChart: () => void;
  onSaveTarget: (target: KpiMetricTarget) => void;
  onDeleteTarget: () => void;
  // Handler HTML5 drag per integrazione con useDragAndDrop (stile Custom Report)
  onDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    id: string,
    title?: string,
  ) => void;
  onDragEnd: () => void;
  onDrag: (event: React.DragEvent<HTMLDivElement>) => void;
}

export const KPICard: React.FC<KPICardProps> = ({
  id,
  metric,
  connectorType,
  dateRange,
  compareDateRange,
  showChart,
  filters = [],
  target,
  onRemove,
  onToggleChart,
  onSaveTarget,
  onDeleteTarget,
  onDragStart,
  onDragEnd,
  onDrag,
}) => {
  const [targetModalOpen, setTargetModalOpen] = useState(false);
  const {
    googleAdsConnectorId,
    shopifyConnectorId,
    magentoConnectorId,
    facebookConnectorId,
    googleAnalytics4ConnectorId,
    clientAccountId,
    propertyId,
    databaseConnectorId,
  } = useConnectors();

  // Crea variabili sempre nuove istanze per forzare Apollo Client a rilevare i cambiamenti
  // Questo risolve il problema di Apollo Client che non rileva cambiamenti in oggetti annidati
  const dateRangeVariables = useMemo(
    () => ({
      from: dateRange.from,
      to: dateRange.to,
    }),
    [dateRange.from, dateRange.to],
  );

  const compareDateRangeVariables = useMemo(
    () =>
      compareDateRange
        ? {
            from: compareDateRange.from,
            to: compareDateRange.to,
          }
        : null,
    [compareDateRange?.from, compareDateRange?.to],
  );
  const databaseDateRangeVariables = useMemo(
    () => ({
      start: dateRange.from,
      end: dateRange.to,
    }),
    [dateRange.from, dateRange.to],
  );
  const compareDatabaseDateRangeVariables = useMemo(
    () =>
      compareDateRange
        ? {
            start: compareDateRange.from,
            end: compareDateRange.to,
          }
        : null,
    [compareDateRange?.from, compareDateRange?.to],
  );

  const isFacebookRatioMetricSelected =
    connectorType === ConnectorType.FACEBOOK &&
    isFacebookRatioMetric(metric.key);
  const isGoogleAdsCpcMetric =
    connectorType === ConnectorType.GOOGLE_ADS &&
    (metric.key === 'metrics.average_cpc' ||
      metric.key === 'average_cpc' ||
      metric.key.endsWith('.average_cpc'));
  const isGoogleAdsCampaignSettingMetricSelected =
    connectorType === ConnectorType.GOOGLE_ADS &&
    isGoogleAdsCampaignSettingMetric(metric.key);

  const shouldBucketByMonth = useMemo(() => {
    const days = daysBetweenInclusive(dateRange.from, dateRange.to);
    // Soglia conservativa: oltre ~4 mesi il daily diventa rumoroso/illeggibile.
    return days > 120;
  }, [dateRange.from, dateRange.to]);

  // Per trend mensile, le metriche "ratio/average" vanno aggregate come media (non somma).
  const isRatioTrendMetric =
    (connectorType === MetricsByConnectorType.KIPIFY &&
      (metric.key === 'markup' ||
        metric.key === 'roas' ||
        metric.key === 'acos' ||
        metric.key === 'acos_total' ||
        metric.key === 'oac' ||
        metric.key === 'cac' ||
        metric.key === 'cac_new_customers')) ||
    (connectorType === ConnectorType.SHOPIFY &&
      (metric.key === 'average_order_value' ||
        metric.key === 'life_time_value' ||
        metric.key === 'discount_rate')) ||
    (connectorType === ConnectorType.MAGENTO &&
      (metric.key === 'average_order_value' ||
        metric.key === 'discount_rate')) ||
    (connectorType === ConnectorType.FACEBOOK &&
      isFacebookRatioMetric(metric.key)) ||
    (connectorType === ConnectorType.GOOGLE_ADS &&
      (metric.key === 'metrics.average_cpc' ||
        metric.key === 'average_cpc' ||
        metric.key.endsWith('.average_cpc') ||
        isGoogleAdsCampaignSettingMetric(metric.key))) ||
    isGoogleAnalytics4RatioMetric(connectorType, metric.key);

  const monthlyAggregation: 'sum' | 'avg' =
    metric.valueType === 'currency' && !isRatioTrendMetric ? 'sum' : 'avg';
  // Target KPI: non moltiplicare setting budget/tCPA per i giorni del periodo
  const shouldNormalizeTargetValue =
    (metric.valueType === 'currency' || metric.valueType === 'number') &&
    !isRatioTrendMetric &&
    !isGoogleAdsCampaignSettingMetricSelected;

  // "Preciso" per mese (Kipify): non media dei ratio giornalieri, ma rapporto tra totali mensili.
  // - ROAS_mese = Σ(price_paid) / Σ(total_marketing_spend)
  // - ACOS_mese = Σ(total_marketing_spend) / Σ(price_paid) * 100
  const exactMonthlyKipifyRatio =
    shouldBucketByMonth &&
    connectorType === MetricsByConnectorType.KIPIFY &&
    (metric.key === 'roas' || metric.key === 'acos');

  // Metriche dove "più basso è meglio": logica invertita per isPositive (colori)
  // OAC, ACOS, ACOS (shipping incl.): freccia = direzione reale (↑ aumentato = male, ↓ calato = bene); % con + e colore da isPositive
  // CAC: freccia su + segno ± come da isPositive (stile precedente)
  const isInvertedMetric =
    connectorType === MetricsByConnectorType.KIPIFY &&
    (metric.key === 'oac' ||
      metric.key === 'acos' ||
      metric.key === 'acos_total' ||
      metric.key === 'cac' ||
      metric.key === 'cac_new_customers');
  const isInvertedTrendDownPlusMetric =
    connectorType === MetricsByConnectorType.KIPIFY &&
    (metric.key === 'oac' ||
      metric.key === 'acos' ||
      metric.key === 'acos_total');
  const isGoogleAdsCostPerConversionMetric =
    connectorType === ConnectorType.GOOGLE_ADS &&
    (metric.key === 'metrics.cost_per_conversion' ||
      metric.key === 'cost_per_conversion' ||
      metric.key.endsWith('.cost_per_conversion'));
  const isGoogleAnalytics4RatioMetricSelected = isGoogleAnalytics4RatioMetric(
    connectorType,
    metric.key,
  );
  const isDatabaseMetric = connectorType === ConnectorType.DATABASE;

  const { data: databaseConfigData } = useQuery(GET_DB_CONNECTOR_CONFIG, {
    skip: !isDatabaseMetric || !databaseConnectorId,
    variables: { connectorId: databaseConnectorId || '' },
    fetchPolicy: 'cache-first',
  });
  const databaseDateColumn =
    databaseConfigData?.getDatabaseConnectorConfig?.dateColumn || null;
  const databaseMetricDailySplit = useMemo(
    () => resolveDatabaseMetricDailySplit(metric.key, databaseDateColumn),
    [metric.key, databaseDateColumn],
  );

  // Query per Google Analytics 4 - includi split per data per avere valori giornalieri
  // Nota: GA4 spesso ritorna date come YYYYMMDD; la normalizzazione avviene in chartData.
  const {
    data: googleAnalytics4Data,
    loading: googleAnalytics4Loading,
    error: googleAnalytics4Error,
  } = useQuery(GET_GOOGLE_ANALYTICS_4_METRICS, {
    skip:
      connectorType !== ConnectorType.GOOGlE_ANALYTICS4 ||
      !googleAnalytics4ConnectorId ||
      !propertyId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['date'],
        filters: [], // Filters apply only to Shopify; GA4 uses different filter format
        dateRange: dateRangeVariables,
      },
      propertyId: propertyId || '',
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query GA4 totale (senza split) per metriche average/ratio.
  // Average purchase revenue è già una media del periodo: sommare i giorni gonfia il KPI.
  const {
    data: googleAnalytics4TotalData,
    loading: googleAnalytics4TotalLoading,
    error: googleAnalytics4TotalError,
  } = useQuery(GET_GOOGLE_ANALYTICS_4_METRICS, {
    skip:
      connectorType !== ConnectorType.GOOGlE_ANALYTICS4 ||
      !googleAnalytics4ConnectorId ||
      !propertyId ||
      !isGoogleAnalytics4RatioMetricSelected,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: [],
        filters: [],
        dateRange: dateRangeVariables,
      },
      propertyId: propertyId || '',
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per Shopify - usa 'order_creation_date' invece di 'date' per avere valori giornalieri
  const {
    data: shopifyData,
    loading: shopifyLoading,
    error: shopifyError,
  } = useQuery(GET_SHOPIFY_METRICS_VALUES, {
    skip: connectorType !== ConnectorType.SHOPIFY || !shopifyConnectorId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['order_creation_date'], // Shopify usa 'order_creation_date' per dati giornalieri
        filters,
        dateRange: dateRangeVariables,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: magentoData,
    loading: magentoLoading,
    error: magentoError,
  } = useQuery(GET_MAGENTO_METRICS_VALUES, {
    skip: connectorType !== ConnectorType.MAGENTO || !magentoConnectorId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['order_creation_date'],
        filters: [],
        dateRange: dateRangeVariables,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per Facebook - includi split per data per avere valori giornalieri
  const {
    data: facebookData,
    loading: facebookLoading,
    error: facebookError,
  } = useQuery(GET_FACEBOOK_METRICS_VALUES, {
    skip: connectorType !== ConnectorType.FACEBOOK || !facebookConnectorId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['date'], // Aggiungi split per data per avere valori giornalieri
        filters: [],
        dateRange: dateRangeVariables,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per Facebook - TOTALE (senza split).
  // Fix: cost-per / rate non vanno sommati sui giorni; serve il valore aggregato sul periodo.
  const {
    data: facebookTotalData,
    loading: facebookTotalLoading,
    error: facebookTotalError,
  } = useQuery(GET_FACEBOOK_METRICS_VALUES, {
    skip:
      connectorType !== ConnectorType.FACEBOOK ||
      !facebookConnectorId ||
      !isFacebookRatioMetricSelected,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: [],
        filters: [],
        dateRange: dateRangeVariables,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per Google Ads - includi split per data per avere valori giornalieri
  const {
    data: googleAdsData,
    loading: googleAdsLoading,
    error: googleAdsError,
  } = useQuery(GET_GOOGLE_ADS_METRICS_DRAFT, {
    skip:
      connectorType !== ConnectorType.GOOGLE_ADS ||
      !googleAdsConnectorId ||
      !clientAccountId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['segments.date'], // Google Ads usa segments.date
        filters: [],
        dateRange: dateRangeVariables,
      },
      clientAccountId: clientAccountId || '',
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per Google Ads - TOTALE (senza split).
  // Fix: alcune metriche (es. Average CPC, Cost per conversion) non vanno sommate sui giorni;
  // serve il valore aggregato sul periodo.
  const {
    data: googleAdsTotalData,
    loading: googleAdsTotalLoading,
    error: googleAdsTotalError,
  } = useQuery(GET_GOOGLE_ADS_METRICS_DRAFT, {
    skip:
      connectorType !== ConnectorType.GOOGLE_ADS ||
      !googleAdsConnectorId ||
      !clientAccountId ||
      !(isGoogleAdsCpcMetric || isGoogleAdsCostPerConversionMetric),
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: [],
        filters: [],
        dateRange: dateRangeVariables,
      },
      clientAccountId: clientAccountId || '',
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Solo Markup (Kipify) applica i filtri del pannello; le altre metriche Kipify restano unfiltered
  const kipifyMarkupUsesFilters = metric.key === 'markup';

  // Query per Kipify - include split date per avere valori giornalieri
  const {
    data: kipifyData,
    loading: kipifyLoading,
    error: kipifyError,
  } = useQuery(GET_KIPIFY_METRICS_VALUES, {
    skip: connectorType !== MetricsByConnectorType.KIPIFY,
    variables: {
      args: {
        metricKeys: exactMonthlyKipifyRatio
          ? [metric.key, 'price_paid', 'total_marketing_spend']
          : [metric.key],
        splitsKeys: ['date'],
        filters: kipifyMarkupUsesFilters ? filters : [],
        dateRange: dateRangeVariables,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per Custom Formulas - TOTALE (senza split).
  // Motivazione: il backend formatta i valori e alcuni getter (es. Shopify) non supportano lo split "date";
  // per il valore card serve una risposta stabile, indipendente dagli split.
  const {
    data: customFormulaTotalData,
    loading: customFormulaTotalLoading,
    error: customFormulaTotalError,
  } = useQuery(GET_CUSTOM_FORMULA_VALUE, {
    skip: connectorType !== MetricsByConnectorType.CUSTOM_FORMULA,
    variables: {
      args: {
        formulaIds: [metric.key],
        splitsKeys: [],
        filters: [], // Filters apply only to Shopify
        dateRange: dateRangeVariables,
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per Custom Formulas - GIORNALIERA (con split date) per il grafico.
  const {
    data: customFormulaDailyData,
    loading: customFormulaDailyLoading,
    error: customFormulaDailyError,
  } = useQuery(GET_CUSTOM_FORMULA_VALUE, {
    skip: connectorType !== MetricsByConnectorType.CUSTOM_FORMULA || !showChart,
    variables: {
      args: {
        formulaIds: [metric.key], // Per custom formulas, metric.key è l'ID della formula
        splitsKeys: ['date'], // Custom formulas supportano split per data come Kipify
        filters: [], // Filters apply only to Shopify
        dateRange: dateRangeVariables,
      },
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: databaseTotalData,
    loading: databaseTotalLoading,
    error: databaseTotalError,
  } = useQuery(GET_DATABASE_DATA_FOR_CUSTOM_REPORT, {
    skip: !isDatabaseMetric || !databaseConnectorId,
    variables: {
      connectorId: databaseConnectorId || '',
      splits: [],
      metrics: [metric.key],
      filters: [],
      dateRange: databaseDateRangeVariables,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: databaseDailyData,
    loading: databaseDailyLoading,
    error: databaseDailyError,
  } = useQuery(GET_DATABASE_DATA_FOR_CUSTOM_REPORT, {
    skip:
      !isDatabaseMetric ||
      !databaseConnectorId ||
      !databaseMetricDailySplit ||
      !showChart,
    variables: {
      connectorId: databaseConnectorId || '',
      splits: databaseMetricDailySplit ? [databaseMetricDailySplit] : [],
      metrics: [metric.key],
      filters: [],
      dateRange: databaseDateRangeVariables,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per periodo di comparazione - Shopify
  const {
    data: compareShopifyData,
    loading: compareShopifyLoading,
    error: compareShopifyError,
  } = useQuery(GET_SHOPIFY_METRICS_VALUES, {
    skip:
      !compareDateRange ||
      connectorType !== ConnectorType.SHOPIFY ||
      !shopifyConnectorId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['order_creation_date'],
        filters,
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: compareMagentoData,
    loading: compareMagentoLoading,
    error: compareMagentoError,
  } = useQuery(GET_MAGENTO_METRICS_VALUES, {
    skip:
      !compareDateRange ||
      connectorType !== ConnectorType.MAGENTO ||
      !magentoConnectorId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['order_creation_date'],
        filters: [],
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per periodo di comparazione - Facebook
  const {
    data: compareFacebookData,
    loading: compareFacebookLoading,
    error: compareFacebookError,
  } = useQuery(GET_FACEBOOK_METRICS_VALUES, {
    skip:
      !compareDateRange ||
      connectorType !== ConnectorType.FACEBOOK ||
      !facebookConnectorId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['date'],
        filters: [],
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per periodo di comparazione - Facebook (TOTALE, senza split).
  // Fix: cost-per / rate non vanno sommati sui giorni anche nel periodo di confronto.
  const {
    data: compareFacebookTotalData,
    loading: compareFacebookTotalLoading,
    error: compareFacebookTotalError,
  } = useQuery(GET_FACEBOOK_METRICS_VALUES, {
    skip:
      !compareDateRange ||
      connectorType !== ConnectorType.FACEBOOK ||
      !facebookConnectorId ||
      !isFacebookRatioMetricSelected,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: [],
        filters: [],
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
    },
    fetchPolicy: 'network-only',
  });

  // Query per periodo di comparazione - Google Ads
  const {
    data: compareGoogleAdsData,
    loading: compareGoogleAdsLoading,
    error: compareGoogleAdsError,
  } = useQuery(GET_GOOGLE_ADS_METRICS_DRAFT, {
    skip:
      !compareDateRange ||
      connectorType !== ConnectorType.GOOGLE_ADS ||
      !googleAdsConnectorId ||
      !clientAccountId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['segments.date'],
        filters: [],
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
      clientAccountId: clientAccountId || '',
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per periodo di comparazione - Google Ads (TOTALE, senza split).
  // Fix: metriche non additive non vanno sommate sui giorni anche nel periodo di confronto.
  const {
    data: compareGoogleAdsTotalData,
    loading: compareGoogleAdsTotalLoading,
    error: compareGoogleAdsTotalError,
  } = useQuery(GET_GOOGLE_ADS_METRICS_DRAFT, {
    skip:
      !compareDateRange ||
      connectorType !== ConnectorType.GOOGLE_ADS ||
      !googleAdsConnectorId ||
      !clientAccountId ||
      !(isGoogleAdsCpcMetric || isGoogleAdsCostPerConversionMetric),
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: [],
        filters: [],
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
      clientAccountId: clientAccountId || '',
    },
    fetchPolicy: 'network-only',
  });

  // Query per periodo di comparazione - Kipify (filtri solo per Markup)
  const {
    data: compareKipifyData,
    loading: compareKipifyLoading,
    error: compareKipifyError,
  } = useQuery(GET_KIPIFY_METRICS_VALUES, {
    skip: !compareDateRange || connectorType !== MetricsByConnectorType.KIPIFY,
    variables: {
      args: {
        metricKeys: exactMonthlyKipifyRatio
          ? [metric.key, 'price_paid', 'total_marketing_spend']
          : [metric.key],
        splitsKeys: ['date'],
        filters: kipifyMarkupUsesFilters ? filters : [],
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per periodo di comparazione - Custom Formulas (TOTALE, senza split).
  // Serve per il delta "% was …" sulla card; il grafico usa la query daily sotto.
  const {
    data: compareCustomFormulaData,
    loading: compareCustomFormulaLoading,
    error: compareCustomFormulaError,
  } = useQuery(GET_CUSTOM_FORMULA_VALUE, {
    skip:
      !compareDateRange ||
      connectorType !== MetricsByConnectorType.CUSTOM_FORMULA,
    variables: {
      args: {
        formulaIds: [metric.key],
        splitsKeys: [],
        filters: [], // Filters apply only to Shopify
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Compare giornaliero per custom formula: senza split date il grafico non ha
  // punti giornalieri (solo totale) → linea di confronto assente/a zero.
  const {
    data: compareCustomFormulaDailyData,
    loading: compareCustomFormulaDailyLoading,
    error: compareCustomFormulaDailyError,
  } = useQuery(GET_CUSTOM_FORMULA_VALUE, {
    skip:
      !compareDateRange ||
      connectorType !== MetricsByConnectorType.CUSTOM_FORMULA ||
      !showChart,
    variables: {
      args: {
        formulaIds: [metric.key],
        splitsKeys: ['date'],
        filters: [],
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: compareDatabaseTotalData,
    loading: compareDatabaseTotalLoading,
    error: compareDatabaseTotalError,
  } = useQuery(GET_DATABASE_DATA_FOR_CUSTOM_REPORT, {
    skip: !compareDateRange || !isDatabaseMetric || !databaseConnectorId,
    variables: {
      connectorId: databaseConnectorId || '',
      splits: [],
      metrics: [metric.key],
      filters: [],
      dateRange: compareDatabaseDateRangeVariables || { start: '', end: '' },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: compareDatabaseDailyData,
    loading: compareDatabaseDailyLoading,
    error: compareDatabaseDailyError,
  } = useQuery(GET_DATABASE_DATA_FOR_CUSTOM_REPORT, {
    skip:
      !compareDateRange ||
      !isDatabaseMetric ||
      !databaseConnectorId ||
      !databaseMetricDailySplit ||
      !showChart,
    variables: {
      connectorId: databaseConnectorId || '',
      splits: databaseMetricDailySplit ? [databaseMetricDailySplit] : [],
      metrics: [metric.key],
      filters: [],
      dateRange: compareDatabaseDateRangeVariables || { start: '', end: '' },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query per periodo di comparazione - Google Analytics 4
  const {
    data: compareGoogleAnalytics4Data,
    loading: compareGoogleAnalytics4Loading,
    error: compareGoogleAnalytics4Error,
  } = useQuery(GET_GOOGLE_ANALYTICS_4_METRICS, {
    skip:
      !compareDateRange ||
      connectorType !== ConnectorType.GOOGlE_ANALYTICS4 ||
      !googleAnalytics4ConnectorId ||
      !propertyId,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: ['date'],
        filters: [],
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
      propertyId: propertyId || '',
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Query GA4 totale anche per il periodo di confronto delle metriche average/ratio.
  const {
    data: compareGoogleAnalytics4TotalData,
    loading: compareGoogleAnalytics4TotalLoading,
    error: compareGoogleAnalytics4TotalError,
  } = useQuery(GET_GOOGLE_ANALYTICS_4_METRICS, {
    skip:
      !compareDateRange ||
      connectorType !== ConnectorType.GOOGlE_ANALYTICS4 ||
      !googleAnalytics4ConnectorId ||
      !propertyId ||
      !isGoogleAnalytics4RatioMetricSelected,
    variables: {
      args: {
        metricKeys: [metric.key],
        splitsKeys: [],
        filters: [],
        dateRange: compareDateRangeVariables || { from: '', to: '' },
      },
      propertyId: propertyId || '',
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // Determina quale data usare
  // Per le custom formula, usa SEMPRE i dati totali per il valore della card
  // e i dati giornalieri SOLO per il grafico (gestito separatamente in chartData)
  const customFormulaData = customFormulaTotalData;
  const data =
    googleAnalytics4Data ||
    shopifyData ||
    magentoData ||
    facebookData ||
    googleAdsData ||
    kipifyData ||
    customFormulaData ||
    databaseDailyData ||
    databaseTotalData;
  const loading =
    googleAnalytics4Loading ||
    shopifyLoading ||
    magentoLoading ||
    facebookLoading ||
    (isFacebookRatioMetricSelected ? facebookTotalLoading : false) ||
    googleAdsLoading ||
    (isGoogleAdsCpcMetric || isGoogleAdsCostPerConversionMetric
      ? googleAdsTotalLoading
      : false) ||
    kipifyLoading ||
    customFormulaTotalLoading ||
    databaseTotalLoading ||
    (isDatabaseMetric ? databaseDailyLoading && showChart : false) ||
    (isGoogleAnalytics4RatioMetricSelected
      ? googleAnalytics4TotalLoading
      : false) ||
    // Compare: attendi sempre i dati di confronto (altrimenti card senza delta/linea)
    compareShopifyLoading ||
    compareMagentoLoading ||
    compareFacebookLoading ||
    (isFacebookRatioMetricSelected ? compareFacebookTotalLoading : false) ||
    compareGoogleAdsLoading ||
    (isGoogleAdsCpcMetric || isGoogleAdsCostPerConversionMetric
      ? compareGoogleAdsTotalLoading
      : false) ||
    compareKipifyLoading ||
    compareCustomFormulaLoading ||
    (connectorType === MetricsByConnectorType.CUSTOM_FORMULA && showChart
      ? compareCustomFormulaDailyLoading
      : false) ||
    compareGoogleAnalytics4Loading ||
    (isGoogleAnalytics4RatioMetricSelected
      ? compareGoogleAnalytics4TotalLoading
      : false) ||
    compareDatabaseTotalLoading ||
    compareDatabaseDailyLoading;
  const error =
    googleAnalytics4Error ||
    shopifyError ||
    magentoError ||
    facebookError ||
    (isFacebookRatioMetricSelected ? facebookTotalError : null) ||
    googleAdsError ||
    (isGoogleAdsCpcMetric || isGoogleAdsCostPerConversionMetric
      ? googleAdsTotalError
      : null) ||
    kipifyError ||
    customFormulaTotalError ||
    databaseTotalError ||
    (isDatabaseMetric && showChart ? databaseDailyError : null) ||
    (isGoogleAnalytics4RatioMetricSelected
      ? googleAnalytics4TotalError
      : null) ||
    compareShopifyError ||
    compareMagentoError ||
    compareFacebookError ||
    (isFacebookRatioMetricSelected ? compareFacebookTotalError : null) ||
    compareGoogleAdsError ||
    (isGoogleAdsCpcMetric || isGoogleAdsCostPerConversionMetric
      ? compareGoogleAdsTotalError
      : null) ||
    compareKipifyError ||
    compareCustomFormulaError ||
    (connectorType === MetricsByConnectorType.CUSTOM_FORMULA && showChart
      ? compareCustomFormulaDailyError
      : null) ||
    compareGoogleAnalytics4Error ||
    (isGoogleAnalytics4RatioMetricSelected
      ? compareGoogleAnalytics4TotalError
      : null) ||
    compareDatabaseTotalError ||
    compareDatabaseDailyError;

  // Per metriche average/ratio usiamo il dato TOTALE per il valore card (senza split),
  // ma manteniamo i dati giornalieri per il grafico.
  const valueData =
    connectorType === ConnectorType.GOOGlE_ANALYTICS4 &&
    isGoogleAnalytics4RatioMetricSelected
      ? googleAnalytics4TotalData
      : connectorType === ConnectorType.FACEBOOK && isFacebookRatioMetricSelected
        ? facebookTotalData
        : connectorType === ConnectorType.GOOGLE_ADS &&
            (isGoogleAdsCpcMetric || isGoogleAdsCostPerConversionMetric)
          ? googleAdsTotalData
          : isDatabaseMetric
            ? databaseTotalData
            : data;

  // Estrai i valori delle metriche dai dati
  const metricValue = useMemo(() => {
    if (!valueData) return null;

    let rows: any[] = [];
    if (
      connectorType === ConnectorType.GOOGlE_ANALYTICS4 &&
      valueData.getGoogleAnalytics4Metrics
    ) {
      rows = Array.isArray(valueData.getGoogleAnalytics4Metrics)
        ? valueData.getGoogleAnalytics4Metrics
        : [valueData.getGoogleAnalytics4Metrics];
    } else if (
      connectorType === ConnectorType.SHOPIFY &&
      valueData.getShopifyMetricValues
    ) {
      rows = valueData.getShopifyMetricValues.metricRows || [];
    } else if (
      connectorType === ConnectorType.MAGENTO &&
      valueData.getMagentoMetricValues
    ) {
      rows = valueData.getMagentoMetricValues.metricRows || [];
    } else if (
      connectorType === ConnectorType.FACEBOOK &&
      valueData.getFacebookMetricsValues
    ) {
      // Facebook restituisce un array diretto o un oggetto con rows
      const fbData = valueData.getFacebookMetricsValues;
      rows = Array.isArray(fbData) ? fbData : fbData.rows || [fbData];
    } else if (
      connectorType === ConnectorType.GOOGLE_ADS &&
      valueData.getGoogleAdsMetrics
    ) {
      // Google Ads restituisce un array diretto
      rows = Array.isArray(valueData.getGoogleAdsMetrics)
        ? valueData.getGoogleAdsMetrics
        : [valueData.getGoogleAdsMetrics];
    } else if (
      connectorType === MetricsByConnectorType.KIPIFY &&
      valueData.getKipifyMetricsValues
    ) {
      const kipifyData = valueData.getKipifyMetricsValues;
      rows = Array.isArray(kipifyData) ? kipifyData : [kipifyData];
    } else if (
      connectorType === MetricsByConnectorType.CUSTOM_FORMULA &&
      valueData.getCustomFormulaValues
    ) {
      // Custom Formulas restituisce un array diretto di MetricValuesRow
      const customFormulaData = valueData.getCustomFormulaValues;
      rows = Array.isArray(customFormulaData)
        ? customFormulaData
        : [customFormulaData];
    } else if (
      connectorType === ConnectorType.DATABASE &&
      valueData.getDatabaseConnectorDataForCustomReport
    ) {
      const dbData = valueData.getDatabaseConnectorDataForCustomReport;
      rows = Array.isArray(dbData) ? dbData : [dbData];
    }

    if (rows.length === 0) return null;

    // Per le custom formula, i dati totali contengono una sola riga senza split date
    // Prendiamo il valore direttamente senza sommare
    if (connectorType === MetricsByConnectorType.CUSTOM_FORMULA) {
      const row = rows[0]; // Dovrebbe esserci solo una riga per i dati totali
      if (row) {
        const metricValue = row.metricValues?.find(
          (mv: any) => mv.key === metric.key,
        );
        if (metricValue?.value) {
          return parseNumericValue(metricValue.value);
        }
      }
      return 0;
    }

    if (connectorType === ConnectorType.DATABASE) {
      const row = rows[0];
      const dbValue = row?.metricValues?.find(
        (mv: any) => mv.key === metric.key,
      )?.value;
      return parseNumericValue(dbValue);
    }

    return computeKpiCardTotalFromRows({
      rows,
      connectorType,
      metricKey: metric.key,
    });
  }, [valueData, connectorType, metric.key]);

  // Calcola il valore comparato (periodo di comparazione)
  const compareMetricValue = useMemo(() => {
    if (!compareDateRange) return null;

    let compareDataSource;
    if (connectorType === ConnectorType.SHOPIFY) {
      compareDataSource = compareShopifyData;
    } else if (connectorType === ConnectorType.MAGENTO) {
      compareDataSource = compareMagentoData;
    } else if (connectorType === ConnectorType.GOOGlE_ANALYTICS4) {
      compareDataSource = isGoogleAnalytics4RatioMetricSelected
        ? compareGoogleAnalytics4TotalData
        : compareGoogleAnalytics4Data;
    } else if (connectorType === ConnectorType.FACEBOOK) {
      // Fix cost-per/rate: usa il dato totale (senza split) per il periodo di confronto
      compareDataSource = isFacebookRatioMetricSelected
        ? compareFacebookTotalData
        : compareFacebookData;
    } else if (connectorType === ConnectorType.GOOGLE_ADS) {
      // Fix metriche non additive: usa il dato totale (senza split) per il periodo di confronto
      compareDataSource =
        isGoogleAdsCpcMetric || isGoogleAdsCostPerConversionMetric
          ? compareGoogleAdsTotalData
          : compareGoogleAdsData;
    } else if (connectorType === MetricsByConnectorType.KIPIFY) {
      compareDataSource = compareKipifyData;
    } else if (connectorType === MetricsByConnectorType.CUSTOM_FORMULA) {
      compareDataSource = compareCustomFormulaData;
    } else if (connectorType === ConnectorType.DATABASE) {
      compareDataSource = compareDatabaseTotalData;
    } else {
      return null;
    }

    if (!compareDataSource) return null;

    let rows: any[] = [];
    if (
      connectorType === ConnectorType.GOOGlE_ANALYTICS4 &&
      compareDataSource.getGoogleAnalytics4Metrics
    ) {
      rows = Array.isArray(compareDataSource.getGoogleAnalytics4Metrics)
        ? compareDataSource.getGoogleAnalytics4Metrics
        : [compareDataSource.getGoogleAnalytics4Metrics];
    } else if (
      connectorType === ConnectorType.SHOPIFY &&
      compareDataSource.getShopifyMetricValues
    ) {
      rows = compareDataSource.getShopifyMetricValues.metricRows || [];
    } else if (
      connectorType === ConnectorType.MAGENTO &&
      compareDataSource.getMagentoMetricValues
    ) {
      rows = compareDataSource.getMagentoMetricValues.metricRows || [];
    } else if (
      connectorType === ConnectorType.FACEBOOK &&
      compareDataSource.getFacebookMetricsValues
    ) {
      const fbData = compareDataSource.getFacebookMetricsValues;
      rows = Array.isArray(fbData) ? fbData : fbData.rows || [fbData];
    } else if (
      connectorType === ConnectorType.GOOGLE_ADS &&
      compareDataSource.getGoogleAdsMetrics
    ) {
      rows = Array.isArray(compareDataSource.getGoogleAdsMetrics)
        ? compareDataSource.getGoogleAdsMetrics
        : [compareDataSource.getGoogleAdsMetrics];
    } else if (
      connectorType === MetricsByConnectorType.KIPIFY &&
      compareDataSource.getKipifyMetricsValues
    ) {
      const kipifyData = compareDataSource.getKipifyMetricsValues;
      rows = Array.isArray(kipifyData) ? kipifyData : [kipifyData];
    } else if (
      connectorType === MetricsByConnectorType.CUSTOM_FORMULA &&
      compareDataSource.getCustomFormulaValues
    ) {
      const customFormulaData = compareDataSource.getCustomFormulaValues;
      rows = Array.isArray(customFormulaData)
        ? customFormulaData
        : [customFormulaData];
    } else if (
      connectorType === ConnectorType.DATABASE &&
      compareDataSource.getDatabaseConnectorDataForCustomReport
    ) {
      const dbData = compareDataSource.getDatabaseConnectorDataForCustomReport;
      rows = Array.isArray(dbData) ? dbData : [dbData];
    }

    if (rows.length === 0) return 0;

    if (connectorType === MetricsByConnectorType.CUSTOM_FORMULA) {
      const row = rows[0];
      if (row) {
        const metricValue = row.metricValues?.find(
          (mv: any) => mv.key === metric.key,
        );
        if (metricValue?.value !== undefined && metricValue?.value !== null) {
          return parseNumericValue(metricValue.value);
        }
      }
      return 0;
    }

    if (connectorType === ConnectorType.DATABASE) {
      const row = rows[0];
      const dbValue = row?.metricValues?.find(
        (mv: any) => mv.key === metric.key,
      )?.value;
      return parseNumericValue(dbValue);
    }

    return computeKpiCardTotalFromRows({
      rows,
      connectorType,
      metricKey: metric.key,
    });
  }, [
    compareDateRange,
    compareShopifyData,
    compareMagentoData,
    compareFacebookData,
    compareFacebookTotalData,
    compareGoogleAdsData,
    compareGoogleAdsTotalData,
    compareGoogleAnalytics4Data,
    compareGoogleAnalytics4TotalData,
    compareKipifyData,
    compareCustomFormulaData,
    compareDatabaseTotalData,
    connectorType,
    metric.key,
    isFacebookRatioMetricSelected,
    isGoogleAdsCpcMetric,
    isGoogleAdsCostPerConversionMetric,
    isGoogleAnalytics4RatioMetricSelected,
  ]);

  // Prepara i dati per il grafico - valori giornalieri
  // Per le custom formula, usa i dati giornalieri se disponibili, altrimenti usa i dati totali
  const chartDataDaily = useMemo((): Array<{ date: string; value: number }> => {
    if (!showChart) return [];

    // Per le custom formula, usa SOLO i dati giornalieri per il grafico
    // Se non sono disponibili, restituisci array vuoto (mostrerà il messaggio "Daily data not available")
    let chartDataSource;
    let rows: any[] = [];

    if (connectorType === MetricsByConnectorType.CUSTOM_FORMULA) {
      // Per le custom formula, usa solo i dati giornalieri (con split date)
      // Se non sono disponibili, non mostriamo il grafico
      if (customFormulaDailyLoading) {
        return []; // Ancora in loading, aspetta
      }
      if (customFormulaDailyError) {
        return [];
      }
      if (
        !customFormulaDailyData ||
        !customFormulaDailyData.getCustomFormulaValues
      ) {
        return [];
      }
      chartDataSource = customFormulaDailyData;
      const customFormulaData = chartDataSource.getCustomFormulaValues;
      rows = Array.isArray(customFormulaData)
        ? customFormulaData
        : [customFormulaData];
    } else if (connectorType === ConnectorType.DATABASE) {
      if (databaseDailyLoading) {
        return [];
      }
      if (databaseDailyError) {
        return [];
      }
      if (!databaseDailyData?.getDatabaseConnectorDataForCustomReport) {
        return [];
      }
      const dbData = databaseDailyData.getDatabaseConnectorDataForCustomReport;
      rows = Array.isArray(dbData) ? dbData : [dbData];
    } else {
      chartDataSource = data;
      if (!chartDataSource) return [];

      if (
        connectorType === ConnectorType.GOOGlE_ANALYTICS4 &&
        chartDataSource.getGoogleAnalytics4Metrics
      ) {
        rows = Array.isArray(chartDataSource.getGoogleAnalytics4Metrics)
          ? chartDataSource.getGoogleAnalytics4Metrics
          : [chartDataSource.getGoogleAnalytics4Metrics];
      } else if (
        connectorType === ConnectorType.SHOPIFY &&
        chartDataSource.getShopifyMetricValues
      ) {
        rows = chartDataSource.getShopifyMetricValues.metricRows || [];
      } else if (
        connectorType === ConnectorType.MAGENTO &&
        chartDataSource.getMagentoMetricValues
      ) {
        rows = chartDataSource.getMagentoMetricValues.metricRows || [];
      } else if (
        connectorType === ConnectorType.FACEBOOK &&
        chartDataSource.getFacebookMetricsValues
      ) {
        const fbData = chartDataSource.getFacebookMetricsValues;
        rows = Array.isArray(fbData) ? fbData : fbData.rows || [fbData];
      } else if (
        connectorType === ConnectorType.GOOGLE_ADS &&
        chartDataSource.getGoogleAdsMetrics
      ) {
        rows = Array.isArray(chartDataSource.getGoogleAdsMetrics)
          ? chartDataSource.getGoogleAdsMetrics
          : [chartDataSource.getGoogleAdsMetrics];
      } else if (
        connectorType === MetricsByConnectorType.KIPIFY &&
        chartDataSource.getKipifyMetricsValues
      ) {
        const kipifyData = chartDataSource.getKipifyMetricsValues;
        rows = Array.isArray(kipifyData) ? kipifyData : [kipifyData];
      }
    }

    if (rows.length === 0) return [];

    // Modalità mensile "precisa" Kipify (ROAS/ACOS):
    // calcoliamo il ratio come rapporto tra totali mensili (non media dei ratio giornalieri).
    if (exactMonthlyKipifyRatio) {
      const monthSums: Record<
        string,
        { pricePaidSum: number; marketingSpendSum: number }
      > = {};

      const normalizeDateToYmd = (dateVal: any): string | null => {
        if (!dateVal || dateVal === 'null') return null;

        // Già in YYYY-MM-DD
        if (typeof dateVal === 'string') {
          const ymdMatch = dateVal.match(/^(\d{4}-\d{2}-\d{2})/);
          if (ymdMatch) return ymdMatch[1];

          // GA/legacy: YYYYMMDD
          const compact = dateVal.match(/^(\d{4})(\d{2})(\d{2})$/);
          if (compact) {
            return `${compact[1]}-${compact[2]}-${compact[3]}`;
          }

          const parsed = new Date(dateVal);
          if (!isNaN(parsed.getTime())) {
            return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
          }
        }

        if (typeof dateVal === 'number') {
          const parsed = new Date(dateVal);
          if (!isNaN(parsed.getTime())) {
            return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
          }
        }

        if (dateVal instanceof Date) {
          return `${dateVal.getFullYear()}-${String(dateVal.getMonth() + 1).padStart(2, '0')}-${String(dateVal.getDate()).padStart(2, '0')}`;
        }

        return null;
      };

      for (const row of rows) {
        const isTotalRow = row.splitValues?.some(
          (sv: any) => sv?.key === '_is_total_row',
        );
        if (isTotalRow) continue;

        const dateValue = row.splitValues?.find(
          (sv: any) =>
            sv &&
            (sv.key === 'order_creation_date' ||
              sv.key === 'date' ||
              sv.key === 'segments.date' ||
              isDatabaseChartDateSplit(sv.key, metric.key, databaseDateColumn)),
        );

        const normalizedYmd = normalizeDateToYmd(dateValue?.value);
        if (!normalizedYmd) continue;

        const monthKey = normalizedYmd.slice(0, 7); // YYYY-MM

        const pricePaidValueRaw = row.metricValues?.find(
          (mv: any) => mv && mv.key === 'price_paid',
        )?.value;
        const marketingSpendValueRaw = row.metricValues?.find(
          (mv: any) => mv && mv.key === 'total_marketing_spend',
        )?.value;

        const pricePaidSum = pricePaidValueRaw
          ? parseNumericValue(pricePaidValueRaw)
          : 0;
        const marketingSpendSum = marketingSpendValueRaw
          ? parseNumericValue(marketingSpendValueRaw)
          : 0;

        if (!monthSums[monthKey]) {
          monthSums[monthKey] = { pricePaidSum: 0, marketingSpendSum: 0 };
        }

        monthSums[monthKey].pricePaidSum += isNaN(pricePaidSum)
          ? 0
          : pricePaidSum;
        monthSums[monthKey].marketingSpendSum += isNaN(marketingSpendSum)
          ? 0
          : marketingSpendSum;
      }

      // Evita "vuoti" nel grafico: includi SEMPRE tutti i mesi del range,
      // anche se backend non ha righe per quel mese (totali = 0).
      const start = new Date(dateRange.from);
      const end = new Date(dateRange.to);
      const monthKeys: string[] = [];

      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const cur = new Date(
          Date.UTC(start.getFullYear(), start.getMonth(), 1),
        );
        const endMonth = new Date(
          Date.UTC(end.getFullYear(), end.getMonth(), 1),
        );
        while (cur <= endMonth) {
          const mk = `${cur.getUTCFullYear()}-${String(cur.getUTCMonth() + 1).padStart(2, '0')}`;
          monthKeys.push(mk);
          cur.setUTCMonth(cur.getUTCMonth() + 1);
        }
      }

      return monthKeys.map((monthKey) => {
        const sums = monthSums[monthKey];
        const revenue = sums?.pricePaidSum ?? 0;
        const spend = sums?.marketingSpendSum ?? 0;

        if (metric.key === 'roas') {
          return { date: monthKey, value: spend > 0 ? revenue / spend : 0 };
        }

        if (metric.key === 'acos') {
          return {
            date: monthKey,
            value: revenue > 0 ? (spend / revenue) * 100 : 0,
          };
        }

        return { date: monthKey, value: 0 };
      });
    }

    // Raggruppa per data (giorno) - estrai solo la parte data (YYYY-MM-DD)
    const groupedByDate = rows.reduce(
      (acc, row) => {
        // Escludi le righe totali (marcate con '_is_total_row')
        const isTotalRow = row.splitValues?.some(
          (sv: any) => sv?.key === '_is_total_row',
        );
        if (isTotalRow) return acc;

        // Cerca la data negli split values (priorità: order_creation_date per Shopify, date, segments.date)
        const dateValue = row.splitValues?.find(
          (sv: any) =>
            sv &&
            (sv.key === 'order_creation_date' || // Shopify
              sv.key === 'date' || // Generico/Facebook/Kipify
              sv.key === 'segments.date' || // Google Ads
              isDatabaseChartDateSplit(sv.key, metric.key, databaseDateColumn)), // Database connector
        );
        let date = dateValue?.value || null;

        // Normalizza la data: estrai solo YYYY-MM-DD
        if (date) {
          // Gestisci timestamp numerici (es. da order_creation_date)
          if (typeof date === 'number') {
            const parsed = new Date(date);
            if (!isNaN(parsed.getTime())) {
              date = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
            } else {
              date = null;
            }
          }
          // Se è una stringa ISO, estrai solo la parte data
          else if (typeof date === 'string') {
            // GA4 può restituire YYYYMMDD (es. 20250119)
            const yyyymmdd = date.match(/^(\d{4})(\d{2})(\d{2})$/);
            if (yyyymmdd) {
              date = `${yyyymmdd[1]}-${yyyymmdd[2]}-${yyyymmdd[3]}`;
            } else {
              const dateMatch = date.match(/^(\d{4}-\d{2}-\d{2})/);
              if (dateMatch) {
                date = dateMatch[1];
              } else {
                // Prova a parsare come data (gestisce anche timestamp stringa)
                const parsed = new Date(date);
                if (!isNaN(parsed.getTime())) {
                  date = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
                } else {
                  date = null;
                }
              }
            }
          }
        }

        // Se non abbiamo una data valida o il backend ha messo "null", saltiamo la riga
        if (!date || date === 'null') return acc;

        // Per le custom formula, metric.key è l'ID della formula
        // I metricValues contengono l'ID della formula come chiave
        const metricValue = row.metricValues?.find(
          (mv: any) => mv && mv.key === metric.key,
        );
        const value = metricValue?.value
          ? parseNumericValue(metricValue.value)
          : 0;

        if (!acc[date]) {
          acc[date] = 0;
        }
        acc[date] += isNaN(value) ? 0 : value;
        return acc;
      },
      {} as Record<string, number>,
    );

    // Crea array di date per il periodo selezionato
    // Manteniamo la logica originale per la griglia giornaliera.
    // Il backend fornisce tipicamente date in formato YYYY-MM-DD,
    // e l'asse X di Recharts si basa su quelle stringhe.
    const startDate = new Date(dateRange.from);
    const endDate = new Date(dateRange.to);
    const allDates: string[] = [];

    // Genera tutte le date del periodo
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      allDates.push(dateStr);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Se il backend non ha restituito alcuna data valida:
    // - Magento: periodo vuoto ma query OK → grafico a zero (evita "Daily data not available")
    // - altri connector: nessun grafico
    if (Object.keys(groupedByDate).length === 0) {
      if (
        connectorType === ConnectorType.MAGENTO &&
        (data as any)?.getMagentoMetricValues &&
        allDates.length > 0
      ) {
        return allDates.map((date) => ({ date, value: 0 }));
      }
      return [];
    }

    // Crea i dati del grafico con tutte le date del periodo
    // Se non c'è un valore per una data, usa 0 (buco reale, non media artificiale)
    return allDates
      .map((date) => ({
        date,
        value: groupedByDate[date] ?? 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [
    data,
    customFormulaDailyData,
    customFormulaDailyLoading,
    customFormulaDailyError,
    databaseDailyData,
    databaseDailyLoading,
    databaseDailyError,
    customFormulaTotalData,
    metric.key,
    databaseDateColumn,
    databaseMetricDailySplit,
    connectorType,
    showChart,
    dateRange,
    exactMonthlyKipifyRatio,
  ]);

  const chartData = useMemo((): KpiPoint[] => {
    if (exactMonthlyKipifyRatio) return chartDataDaily;
    if (!shouldBucketByMonth) return chartDataDaily;
    return bucketPointsByMonth({
      points: chartDataDaily,
      aggregation: monthlyAggregation,
    });
  }, [
    chartDataDaily,
    monthlyAggregation,
    shouldBucketByMonth,
    exactMonthlyKipifyRatio,
  ]);

  // Calcola i dati giornalieri del periodo di comparazione per il tooltip
  // IMPORTANT: per il grafico servono sempre i dati con split date (giornalieri),
  // anche per CPC che per il valore totale usa dati senza split
  const compareChartData = useMemo((): Array<{
    date: string;
    value: number;
  }> => {
    if (!compareDateRange || !showChart || chartDataDaily.length === 0)
      return [];

    let compareDataSource;
    if (connectorType === ConnectorType.SHOPIFY) {
      compareDataSource = compareShopifyData;
    } else if (connectorType === ConnectorType.MAGENTO) {
      compareDataSource = compareMagentoData;
    } else if (connectorType === ConnectorType.GOOGlE_ANALYTICS4) {
      compareDataSource = compareGoogleAnalytics4Data;
    } else if (connectorType === ConnectorType.FACEBOOK) {
      // Per il grafico usiamo sempre i dati con split date (giornalieri)
      // anche se per CPC il valore totale usa compareFacebookTotalData
      compareDataSource = compareFacebookData;
    } else if (connectorType === ConnectorType.GOOGLE_ADS) {
      // Per il grafico usiamo sempre i dati con split date (giornalieri)
      // anche se per Average CPC il valore totale usa compareGoogleAdsTotalData
      compareDataSource = compareGoogleAdsData;
    } else if (connectorType === MetricsByConnectorType.KIPIFY) {
      compareDataSource = compareKipifyData;
    } else if (connectorType === MetricsByConnectorType.CUSTOM_FORMULA) {
      // Come il periodo corrente: grafico = daily (split date), non il totale.
      compareDataSource = compareCustomFormulaDailyData;
    } else if (connectorType === ConnectorType.DATABASE) {
      compareDataSource = compareDatabaseDailyData;
    } else {
      return [];
    }

    if (!compareDataSource) return [];

    let rows: any[] = [];
    if (
      connectorType === ConnectorType.GOOGlE_ANALYTICS4 &&
      compareDataSource.getGoogleAnalytics4Metrics
    ) {
      rows = Array.isArray(compareDataSource.getGoogleAnalytics4Metrics)
        ? compareDataSource.getGoogleAnalytics4Metrics
        : [compareDataSource.getGoogleAnalytics4Metrics];
    } else if (
      connectorType === ConnectorType.SHOPIFY &&
      compareDataSource.getShopifyMetricValues
    ) {
      rows = compareDataSource.getShopifyMetricValues.metricRows || [];
    } else if (
      connectorType === ConnectorType.MAGENTO &&
      compareDataSource.getMagentoMetricValues
    ) {
      rows = compareDataSource.getMagentoMetricValues.metricRows || [];
    } else if (
      connectorType === ConnectorType.FACEBOOK &&
      compareDataSource.getFacebookMetricsValues
    ) {
      const fbData = compareDataSource.getFacebookMetricsValues;
      rows = Array.isArray(fbData) ? fbData : fbData.rows || [fbData];
    } else if (
      connectorType === ConnectorType.GOOGLE_ADS &&
      compareDataSource.getGoogleAdsMetrics
    ) {
      rows = Array.isArray(compareDataSource.getGoogleAdsMetrics)
        ? compareDataSource.getGoogleAdsMetrics
        : [compareDataSource.getGoogleAdsMetrics];
    } else if (
      connectorType === MetricsByConnectorType.KIPIFY &&
      compareDataSource.getKipifyMetricsValues
    ) {
      const kipifyData = compareDataSource.getKipifyMetricsValues;
      rows = Array.isArray(kipifyData) ? kipifyData : [kipifyData];
    } else if (
      connectorType === MetricsByConnectorType.CUSTOM_FORMULA &&
      compareDataSource.getCustomFormulaValues
    ) {
      const customFormulaData = compareDataSource.getCustomFormulaValues;
      rows = Array.isArray(customFormulaData)
        ? customFormulaData
        : [customFormulaData];
    } else if (
      connectorType === ConnectorType.DATABASE &&
      compareDataSource.getDatabaseConnectorDataForCustomReport
    ) {
      const dbData = compareDataSource.getDatabaseConnectorDataForCustomReport;
      rows = Array.isArray(dbData) ? dbData : [dbData];
    }

    if (rows.length === 0) {
      // Query compare completata ma senza righe (es. nessun ads nel periodo):
      // allinea a 0 così il grafico mostra la linea di confronto e il delta "+100% was 0".
      return chartDataDaily.map(({ date }) => ({ date, value: 0 }));
    }

    // Mensile "preciso" Kipify (ROAS/ACOS) anche nel confronto.
    if (exactMonthlyKipifyRatio) {
      const monthSums: Record<
        string,
        { pricePaidSum: number; marketingSpendSum: number }
      > = {};

      const normalizeDateToYmd = (dateVal: any): string | null => {
        if (!dateVal || dateVal === 'null') return null;

        if (typeof dateVal === 'string') {
          const ymdMatch = dateVal.match(/^(\d{4}-\d{2}-\d{2})/);
          if (ymdMatch) return ymdMatch[1];

          const compact = dateVal.match(/^(\d{4})(\d{2})(\d{2})$/);
          if (compact) {
            return `${compact[1]}-${compact[2]}-${compact[3]}`;
          }

          const parsed = new Date(dateVal);
          if (!isNaN(parsed.getTime())) {
            return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
          }
        }

        if (typeof dateVal === 'number') {
          const parsed = new Date(dateVal);
          if (!isNaN(parsed.getTime())) {
            return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
          }
        }

        if (dateVal instanceof Date) {
          return `${dateVal.getFullYear()}-${String(dateVal.getMonth() + 1).padStart(2, '0')}-${String(dateVal.getDate()).padStart(2, '0')}`;
        }

        return null;
      };

      for (const row of rows) {
        const isTotalRow = row.splitValues?.some(
          (sv: any) => sv?.key === '_is_total_row',
        );
        if (isTotalRow) continue;

        const dateValue = row.splitValues?.find(
          (sv: any) =>
            sv &&
            (sv.key === 'order_creation_date' ||
              sv.key === 'date' ||
              sv.key === 'segments.date' ||
              isDatabaseChartDateSplit(sv.key, metric.key, databaseDateColumn)),
        );

        const normalizedYmd = normalizeDateToYmd(dateValue?.value);
        if (!normalizedYmd) continue;

        const monthKey = normalizedYmd.slice(0, 7);

        const pricePaidValueRaw = row.metricValues?.find(
          (mv: any) => mv && mv.key === 'price_paid',
        )?.value;
        const marketingSpendValueRaw = row.metricValues?.find(
          (mv: any) => mv && mv.key === 'total_marketing_spend',
        )?.value;

        const pricePaidSum = pricePaidValueRaw
          ? parseNumericValue(pricePaidValueRaw)
          : 0;
        const marketingSpendSum = marketingSpendValueRaw
          ? parseNumericValue(marketingSpendValueRaw)
          : 0;

        if (!monthSums[monthKey]) {
          monthSums[monthKey] = { pricePaidSum: 0, marketingSpendSum: 0 };
        }

        monthSums[monthKey].pricePaidSum += isNaN(pricePaidSum)
          ? 0
          : pricePaidSum;
        monthSums[monthKey].marketingSpendSum += isNaN(marketingSpendSum)
          ? 0
          : marketingSpendSum;
      }

      // Allinea la serie di compare alle stesse X-axis date del periodo corrente.
      // Così ChartWrapper non inventa punti "Value=0" su mesi che esistono solo in compare.
      const currentStartParts = toUtcDateOnly(dateRange.from);
      const currentEndParts = toUtcDateOnly(dateRange.to);
      const compareStartParts = toUtcDateOnly(compareDateRange.from);
      const compareEndParts = toUtcDateOnly(compareDateRange.to);

      const currentStart = currentStartParts
        ? new Date(
            Date.UTC(
              currentStartParts.y,
              currentStartParts.m - 1,
              currentStartParts.d,
            ),
          )
        : new Date(dateRange.from);
      const currentEnd = currentEndParts
        ? new Date(
            Date.UTC(
              currentEndParts.y,
              currentEndParts.m - 1,
              currentEndParts.d,
            ),
          )
        : new Date(dateRange.to);
      const compareStart = compareStartParts
        ? new Date(
            Date.UTC(
              compareStartParts.y,
              compareStartParts.m - 1,
              compareStartParts.d,
            ),
          )
        : new Date(compareDateRange.from);
      const compareEnd = compareEndParts
        ? new Date(
            Date.UTC(
              compareEndParts.y,
              compareEndParts.m - 1,
              compareEndParts.d,
            ),
          )
        : new Date(compareDateRange.to);

      const msDay = 1000 * 60 * 60 * 24;
      const currentDays = Math.floor(
        (currentEnd.getTime() - currentStart.getTime()) / msDay,
      );
      const compareDays = Math.floor(
        (compareEnd.getTime() - compareStart.getTime()) / msDay,
      );

      return chartDataDaily.map(({ date: monthKey }) => {
        const [yy, mm] = monthKey.split('-').map((v) => Number(v));
        const monthStart =
          !Number.isNaN(yy) && !Number.isNaN(mm)
            ? new Date(Date.UTC(yy, mm - 1, 1))
            : currentStart;

        const dayIndex = Math.floor(
          (monthStart.getTime() - currentStart.getTime()) / msDay,
        );
        const normalizedIndex =
          compareDays > 0 && currentDays > 0
            ? (dayIndex / currentDays) * compareDays
            : 0;

        const compareDate = new Date(compareStart);
        compareDate.setUTCDate(
          compareDate.getUTCDate() + Math.round(normalizedIndex),
        );

        const compareMonthKey = `${compareDate.getUTCFullYear()}-${String(compareDate.getUTCMonth() + 1).padStart(2, '0')}`;
        const sums = monthSums[compareMonthKey];
        const revenue = sums?.pricePaidSum ?? 0;
        const spend = sums?.marketingSpendSum ?? 0;

        if (metric.key === 'roas') {
          return { date: monthKey, value: spend > 0 ? revenue / spend : 0 };
        }

        if (metric.key === 'acos') {
          return {
            date: monthKey,
            value: revenue > 0 ? (spend / revenue) * 100 : 0,
          };
        }

        return { date: monthKey, value: 0 };
      });
    }

    // Raggruppa per data (stessa logica di chartData)
    const groupedByDate = rows.reduce(
      (acc, row) => {
        const isTotalRow = row.splitValues?.some(
          (sv: any) => sv?.key === '_is_total_row',
        );
        if (isTotalRow) return acc;

        const dateValue = row.splitValues?.find(
          (sv: any) =>
            sv &&
            (sv.key === 'order_creation_date' ||
              sv.key === 'date' ||
              sv.key === 'segments.date' ||
              isDatabaseChartDateSplit(sv.key, metric.key, databaseDateColumn)),
        );
        let date = dateValue?.value || null;

        if (date) {
          if (typeof date === 'number') {
            const parsed = new Date(date);
            if (!isNaN(parsed.getTime())) {
              date = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
            } else {
              date = null;
            }
          } else if (typeof date === 'string') {
            const yyyymmdd = date.match(/^(\d{4})(\d{2})(\d{2})$/);
            if (yyyymmdd) {
              date = `${yyyymmdd[1]}-${yyyymmdd[2]}-${yyyymmdd[3]}`;
            } else {
              const dateMatch = date.match(/^(\d{4}-\d{2}-\d{2})/);
              if (dateMatch) {
                date = dateMatch[1];
              } else {
                const parsed = new Date(date);
                if (!isNaN(parsed.getTime())) {
                  date = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
                } else {
                  date = null;
                }
              }
            }
          }
        }

        if (!date || date === 'null') return acc;

        const metricValue = row.metricValues?.find(
          (mv: any) => mv && mv.key === metric.key,
        );
        const value = metricValue?.value
          ? parseNumericValue(metricValue.value)
          : 0;

        if (!acc[date]) {
          acc[date] = 0;
        }
        acc[date] += isNaN(value) ? 0 : value;
        return acc;
      },
      {} as Record<string, number>,
    );

    // Mappa ogni giorno del periodo corrente con il giorno corrispondente nel periodo di comparazione
    // basandosi sulla posizione relativa nel range (date-only UTC-safe, no new Date('YYYY-MM-DD')).
    const currentStartParts = toUtcDateOnly(dateRange.from);
    const currentEndParts = toUtcDateOnly(dateRange.to);
    const compareStartParts = toUtcDateOnly(compareDateRange.from);
    const compareEndParts = toUtcDateOnly(compareDateRange.to);

    const currentStart = currentStartParts
      ? new Date(
          Date.UTC(
            currentStartParts.y,
            currentStartParts.m - 1,
            currentStartParts.d,
          ),
        )
      : new Date(dateRange.from);
    const currentEnd = currentEndParts
      ? new Date(
          Date.UTC(currentEndParts.y, currentEndParts.m - 1, currentEndParts.d),
        )
      : new Date(dateRange.to);
    const compareStart = compareStartParts
      ? new Date(
          Date.UTC(
            compareStartParts.y,
            compareStartParts.m - 1,
            compareStartParts.d,
          ),
        )
      : new Date(compareDateRange.from);
    const compareEnd = compareEndParts
      ? new Date(
          Date.UTC(compareEndParts.y, compareEndParts.m - 1, compareEndParts.d),
        )
      : new Date(compareDateRange.to);

    const msDay = 1000 * 60 * 60 * 24;
    const currentDays = Math.floor(
      (currentEnd.getTime() - currentStart.getTime()) / msDay,
    );
    const compareDays = Math.floor(
      (compareEnd.getTime() - compareStart.getTime()) / msDay,
    );

    // Crea mappa: data corrente -> valore comparato
    const compareMap: Record<string, number> = {};

    chartDataDaily.forEach(({ date }) => {
      const currentDateParts = toUtcDateOnly(date);
      const currentDate = currentDateParts
        ? new Date(
            Date.UTC(
              currentDateParts.y,
              currentDateParts.m - 1,
              currentDateParts.d,
            ),
          )
        : new Date(date);
      const dayIndex = Math.floor(
        (currentDate.getTime() - currentStart.getTime()) / msDay,
      );

      // Calcola la data corrispondente nel periodo di comparazione
      // basandosi sulla posizione relativa (es. primo giorno -> primo giorno)
      const normalizedIndex =
        compareDays > 0 && currentDays > 0
          ? (dayIndex / currentDays) * compareDays
          : 0;
      const compareDate = new Date(compareStart);
      compareDate.setUTCDate(
        compareDate.getUTCDate() + Math.round(normalizedIndex),
      );

      const compareDateStr = `${compareDate.getUTCFullYear()}-${String(compareDate.getUTCMonth() + 1).padStart(2, '0')}-${String(compareDate.getUTCDate()).padStart(2, '0')}`;
      compareMap[date] = groupedByDate[compareDateStr] ?? 0;
    });

    const alignedDaily = chartDataDaily.map(({ date }) => ({
      date,
      value: compareMap[date] ?? 0,
    }));

    if (!shouldBucketByMonth) return alignedDaily;
    return bucketPointsByMonth({
      points: alignedDaily,
      aggregation: monthlyAggregation,
    });
  }, [
    compareDateRange,
    compareShopifyData,
    compareMagentoData,
    compareFacebookData,
    compareFacebookTotalData,
    compareGoogleAdsData,
    compareGoogleAdsTotalData,
    compareGoogleAnalytics4Data,
    compareKipifyData,
    compareCustomFormulaDailyData,
    compareDatabaseDailyData,
    metric.key,
    databaseDateColumn,
    databaseMetricDailySplit,
    connectorType,
    metric.key,
    showChart,
    chartDataDaily,
    dateRange,
    isFacebookRatioMetricSelected,
    isGoogleAdsCpcMetric,
    exactMonthlyKipifyRatio,
    shouldBucketByMonth,
    monthlyAggregation,
  ]);

  // Calcola la variazione rispetto al periodo di comparazione
  const change = useMemo(
    () =>
      computeKpiCompareChange({
        metricValue,
        compareMetricValue,
        isInvertedMetric,
      }),
    [metricValue, compareMetricValue, isInvertedMetric],
  );

  const formattedChangePercentage = useMemo(() => {
    if (!change) return null;
    const absolutePercentage = Math.abs(change.percentage);
    // OAC / ACOS / ACOS_total: sempre + e modulo (verde/arancione = miglioramento / peggioramento)
    const sign = isInvertedTrendDownPlusMetric || change.isPositive ? '+' : '-';
    return `${sign}${formatNumberIT(absolutePercentage, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  }, [change, isInvertedTrendDownPlusMetric]);

  const formatValue = (value: number | null): string => {
    if (value === null || value === undefined) return 'N/A';
    if (metric.valueType === 'currency') {
      return `€${formatNumberIT(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    if (
      metric.valueType === 'percentage' ||
      metric.key === 'acos' ||
      metric.key === 'acos_total'
    ) {
      return `${formatNumberIT(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`;
    }
    if (metric.valueType === 'number') {
      const hasDecimals = value % 1 !== 0;
      return formatNumberIT(value, {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      });
    }

    // Fallback: alcune metriche (es. GA4 "computed") possono arrivare senza `valueType`.
    // In quel caso applichiamo comunque una formattazione numerica standard (stile IT)
    // per evitare output "nudi" tipo 378678.
    const hasDecimals = value % 1 !== 0;
    return formatNumberIT(value, {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0,
    });
  };

  const getConnectorIcon = () => {
    const icon = getIconForMetric(
      connectorType as ConnectorType | MetricsByConnectorType,
    );
    const colors: Record<string, string> = {
      [ConnectorType.SHOPIFY]: '#95bf47',
      [ConnectorType.MAGENTO]: '#ee6723',
      [ConnectorType.FACEBOOK]: '#1877f2',
      [ConnectorType.GOOGLE_ADS]: '#4285f4',
      [ConnectorType.GOOGlE_ANALYTICS4]: '#fbbc04',
      [MetricsByConnectorType.KIPIFY]: '#1EED9F',
    };
    return { icon, color: colors[connectorType] || '#667eea' };
  };

  const connectorIcon = getConnectorIcon();
  const isTargetReached = useMemo(() => {
    if (!target) return null;
    return isTargetOnTrack({
      value: metricValue,
      target,
      dateRange,
      normalizeByPeriod: shouldNormalizeTargetValue,
    });
  }, [dateRange, metricValue, shouldNormalizeTargetValue, target]);

  const targetOffTrackGapAmount = useMemo(() => {
    if (!target) return null;
    return getTargetOffTrackGapAmount({
      value: metricValue,
      target,
      dateRange,
      normalizeByPeriod: shouldNormalizeTargetValue,
    });
  }, [dateRange, metricValue, shouldNormalizeTargetValue, target]);

  // Titolo leggibile per il drag shadow (come Custom Report)
  const dragTitle = metric.label || metric.key || id;

  return (
    <Card
      draggable
      onDragStart={(event) => onDragStart(event, id, dragTitle)}
      onDragEnd={onDragEnd}
      onDrag={onDrag}
      sx={{
        cursor: 'grab',
      }}
    >
      <CardHeader>
        <MetricInfo>
          <ConnectorIcons>
            <ConnectorIcon color={connectorIcon.color}>
              {connectorIcon.icon}
            </ConnectorIcon>
          </ConnectorIcons>
          <Box sx={{ flex: 1 }}>
            <MetricLabel>
              {metric.label}
              <Tooltip
                title={
                  metric.key === 'acos' ? (
                    <Box sx={{ p: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 0.5, color: '#000' }}
                      >
                        ACOS
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: '#000' }}
                      >
                        Ad Spend / Revenue × 100
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: '#666',
                          fontSize: '0.7rem',
                        }}
                      >
                        Revenue = products (without shipping)
                      </Typography>
                    </Box>
                  ) : metric.key === 'acos_total' ? (
                    <Box sx={{ p: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 0.5, color: '#000' }}
                      >
                        ACOS (shipping incl.)
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: '#000' }}
                      >
                        Ad Spend / (Revenue + Shipping) × 100
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.5,
                          color: '#666',
                          fontSize: '0.7rem',
                        }}
                      >
                        Revenue = products + shipping
                      </Typography>
                    </Box>
                  ) : (
                    metric.label
                  )
                }
                arrow
                placement="top"
              >
                <InfoIcon
                  sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }}
                />
              </Tooltip>
            </MetricLabel>
          </Box>
        </MetricInfo>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={target ? 'Edit target' : 'Add target'}>
            <IconButton
              size="small"
              onClick={() => setTargetModalOpen(true)}
              sx={{ color: target ? '#5b4df5' : 'text.secondary' }}
            >
              <TrackChangesOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={onToggleChart}
            sx={{ opacity: showChart ? 1 : 0.5 }}
          >
            <Typography variant="caption">📊</Typography>
          </IconButton>
          <IconButton size="small" onClick={onRemove}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </CardHeader>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body2">
          Error loading data
        </Typography>
      ) : (
        <>
          <MetricValue>{formatValue(metricValue)}</MetricValue>
          {change && compareDateRange && (
            <ChangeIndicator>
              {/* OAC, ACOS, ACOS_total: ↑ se il valore è aumentato (negativo), ↓ se calato (positivo); CAC: freccia su + segno ± */}
              {isInvertedMetric ? (
                isInvertedTrendDownPlusMetric ? (
                  change.percentage > 0 ? (
                    <TrendingUpIcon
                      sx={{ color: 'warning.main', fontSize: 18 }}
                    />
                  ) : change.percentage < 0 ? (
                    <TrendingDownIcon
                      sx={{ color: 'success.main', fontSize: 18 }}
                    />
                  ) : (
                    <TrendingFlatIcon
                      sx={{ color: 'text.secondary', fontSize: 18 }}
                    />
                  )
                ) : (
                  <TrendingUpIcon
                    sx={{
                      color: change.isPositive
                        ? 'success.main'
                        : 'warning.main',
                      fontSize: 18,
                    }}
                  />
                )
              ) : change.isPositive ? (
                <TrendingUpIcon sx={{ color: 'success.main', fontSize: 18 }} />
              ) : (
                <TrendingDownIcon
                  sx={{ color: 'warning.main', fontSize: 18 }}
                />
              )}
              <Typography
                variant="body2"
                color={change.isPositive ? 'success.main' : 'warning.main'}
                sx={{ fontWeight: 600 }}
              >
                {formattedChangePercentage}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.75rem', ml: 0.5 }}
              >
                was {formatValue(change.value)}
              </Typography>
            </ChangeIndicator>
          )}

          <Box
            sx={{
              mt: 1.25,
              pt: 1,
              borderTop: '1px solid rgba(148, 163, 184, 0.14)',
            }}
          >
            {target ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 0.75,
                    flexWrap: 'wrap',
                    minWidth: 0,
                  }}
                >
                  <TrackChangesOutlinedIcon
                    sx={{ fontSize: 14, color: '#7c7aa8', flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: '#7c7aa8',
                      fontWeight: 700,
                      letterSpacing: 0.2,
                    }}
                  >
                    {`Target ${getKpiTargetPeriodLabel(target.period).toLowerCase()}`}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 800,
                      color: '#2f2b55',
                      lineHeight: 1.1,
                    }}
                  >
                    {formatValue(target.value)}
                  </Typography>
                  {isTargetReached === false &&
                    targetOffTrackGapAmount !== null && (
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: '#8a88b3',
                          fontWeight: 600,
                        }}
                      >
                        {target.direction === 'at_or_above'
                          ? `Missing ${formatValue(targetOffTrackGapAmount)}`
                          : `Over by ${formatValue(targetOffTrackGapAmount)}`}
                      </Typography>
                    )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    flexShrink: 0,
                  }}
                >
                  <Chip
                    label={
                      isTargetReached === null
                        ? 'No data'
                        : isTargetReached
                          ? 'On track'
                          : 'Off track'
                    }
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      '& .MuiChip-label': { px: 1 },
                      backgroundColor:
                        isTargetReached === null
                          ? 'rgba(148, 163, 184, 0.14)'
                          : isTargetReached
                            ? 'rgba(34, 197, 94, 0.12)'
                            : 'rgba(245, 158, 11, 0.14)',
                      color:
                        isTargetReached === null
                          ? '#475569'
                          : isTargetReached
                            ? '#15803d'
                            : '#b45309',
                      fontWeight: 700,
                    }}
                  />
                  <Tooltip title="Edit target">
                    <IconButton
                      size="small"
                      onClick={() => setTargetModalOpen(true)}
                      sx={{ color: '#6f6c9a', p: 0.35 }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <TrackChangesOutlinedIcon
                    sx={{ fontSize: 14, color: '#9b99b8', flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      color: '#9b99b8',
                      fontWeight: 600,
                    }}
                  >
                    No target
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => setTargetModalOpen(true)}
                  startIcon={<TrackChangesOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    minHeight: 28,
                    px: 1,
                    fontSize: '0.72rem',
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#5146e5',
                  }}
                >
                  Add
                </Button>
              </Box>
            )}
          </Box>

          {showChart && (
            <>
              {chartData.length > 0 ? (
                <ChartContainer>
                  <ChartWrapper
                    data={chartData}
                    compareData={compareChartData}
                    formatValue={formatValue}
                    metric={metric}
                    connectorType={connectorType}
                    dateRange={dateRange}
                    compareDateRange={compareDateRange}
                  />
                </ChartContainer>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '200px',
                    color: 'text.secondary',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2">
                    Daily data not available
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {connectorType === MetricsByConnectorType.CUSTOM_FORMULA
                      ? customFormulaDailyLoading
                        ? 'Loading daily data...'
                        : customFormulaDailyError
                          ? 'Error loading daily data'
                          : 'The backend does not return date split for this custom formula'
                      : 'The backend does not return date split for this metric'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </>
      )}

      <MetricTargetModal
        open={targetModalOpen}
        metric={metric}
        target={target}
        onClose={() => setTargetModalOpen(false)}
        onSave={(nextTarget) => {
          onSaveTarget(nextTarget);
          setTargetModalOpen(false);
        }}
        onDelete={() => {
          onDeleteTarget();
          setTargetModalOpen(false);
        }}
      />
    </Card>
  );
};
